import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  User, 
  Trash2, 
  Download, 
  Loader2,
  RefreshCcw,
  LogOut,
  Monitor,
  LayoutGrid,
  ShieldCheck,
  Video,
  Play
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:5001' : '';

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { type: "spring", stiffness: 100, damping: 15 }
  },
  exit: { opacity: 0, scale: 0.9, transition: { duration: 0.2 } }
};

function App() {
  const [pin, setPin] = useState('');
  const [authCode, setAuthCode] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [status, setStatus] = useState({});
  const [newUsername, setNewUsername] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [imgError, setImgError] = useState({});
  const [activeTab, setActiveTab] = useState('monitoring');

  const fetchData = useCallback(async (code) => {
    const config = { headers: { 'x-access-code': code } };
    try {
      const [favs, recs, stat] = await Promise.all([
        axios.get(`${API_BASE}/api/favorites`, config),
        axios.get(`${API_BASE}/api/recordings`, config),
        axios.get(`${API_BASE}/api/status`, config)
      ]);
      setFavorites(favs.data);
      setRecordings(recs.data);
      setStatus(stat.data);
    } catch (err) {
      console.error('Fetch error:', err);
    }
  }, []);

  const checkAccess = useCallback(async (code) => {
    try {
      const res = await axios.post(`${API_BASE}/api/login`, { code });
      if (res.data.success) {
        setIsAuthorized(true);
        setAuthCode(code);
        fetchData(code);
      } else {
        setError('Accès refusé');
        setAuthCode(null);
      }
    } catch (err) {
      setError("Erreur d'authentification");
    } finally {
      setIsLoading(false);
    }
  }, [fetchData]);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (isAuthorized && authCode) {
      const interval = setInterval(() => {
        fetchData(authCode);
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthorized, authCode, fetchData]);

  const handleLogin = (e) => {
    e.preventDefault();
    checkAccess(pin);
  };

  const handleLogout = () => {
    setAuthCode(null);
    setIsAuthorized(false);
    setPin('');
  };

  const addFavorite = async () => {
    if (!newUsername) return;
    try {
      await axios.post(`${API_BASE}/api/favorites`, { username: newUsername }, { headers: { 'x-access-code': authCode } });
      setNewUsername('');
      fetchData(authCode);
    } catch (err) {
      alert("Erreur lors de l'ajout");
    }
  };

  const removeFavorite = async (username) => {
    try {
      await axios.delete(`${API_BASE}/api/favorites/${username}`, { headers: { 'x-access-code': authCode } });
      fetchData(authCode);
    } catch (err) {
      alert("Erreur lors de la suppression");
    }
  };

  const deleteRecording = async (filename) => {
    if (!window.confirm('Supprimer définitivement cette archive ?')) return;
    try {
      await axios.delete(`${API_BASE}/api/recordings/${filename}`, { headers: { 'x-access-code': authCode } });
      fetchData(authCode);
    } catch (err) {
      alert("Erreur lors de la suppression");
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-black">
        <Loader2 className="text-[#ff0050] animate-spin" size={48} strokeWidth={1.5} />
      </div>
    );
  }

  // Common Background Elements
  const AmbientBackground = () => (
    <>
      <div className="noise-overlay" />
      <div className="ambient-lighting">
        <div className="glow-orb orb-1" />
        <div className="glow-orb orb-2" />
      </div>
    </>
  );

  if (!isAuthorized) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-black px-4 relative overflow-hidden">
        <AmbientBackground />
        
        <motion.div 
          initial={{ opacity: 0, y: 40 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="auth-terminal relative z-10"
        >
          <div className="text-center mb-10">
            <motion.div 
              animate={{ boxShadow: ['0 0 0 rgba(255,0,80,0)', '0 0 40px rgba(255,0,80,0.5)', '0 0 0 rgba(255,0,80,0)'] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="w-16 h-16 bg-[#111] border border-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6"
            >
              <ShieldCheck className="text-white" size={32} strokeWidth={1.5} />
            </motion.div>
            <h2 className="text-4xl text-white">SYSTEM_LOCK</h2>
            <p className="kicker mt-2 text-[#ff0050]">ACCÈS RESTREINT</p>
          </div>
          
          <form onSubmit={handleLogin}>
            <input 
              type="password" 
              className="pin-input" 
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="••••"
              maxLength={4}
              autoFocus
            />
            <div className="h-6 mt-4 text-center">
              {error && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[#ff0050] text-xs font-bold uppercase tracking-widest">{error}</motion.p>}
            </div>
            <button type="submit" className="auth-btn">
              DÉVERROUILLER
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="app-shell bg-black">
      <AmbientBackground />
      
      {/* Floating Sidebar */}
      <motion.aside 
        initial={{ x: -50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="sidebar-floating"
      >
        <div className="sidebar-logo">
          <div className="logo-mark">
            <Video size={20} strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-xl">LIVEVAULT</h1>
            <p className="kicker">Régie Centrale</p>
          </div>
        </div>

        <nav className="nav-group">
          <button onClick={() => setActiveTab('monitoring')} className={`nav-btn ${activeTab === 'monitoring' ? 'active' : ''}`}>
            <Monitor size={18} /> Surveillance
          </button>
          <button onClick={() => setActiveTab('library')} className={`nav-btn ${activeTab === 'library' ? 'active' : ''}`}>
            <LayoutGrid size={18} /> Vidéothèque
          </button>
        </nav>

        <div className="mt-auto flex flex-col gap-2">
          <button onClick={() => fetchData(authCode)} className="nav-btn">
            <RefreshCcw size={18} /> Actualiser
          </button>
          <button onClick={handleLogout} className="nav-btn danger">
            <LogOut size={18} /> Déconnexion
          </button>
        </div>
      </motion.aside>

      {/* Main Content View */}
      <main className="main-view relative z-10 w-full pl-6">
        <motion.header 
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="header-bar"
        >
          <div>
            <h2>{activeTab === 'monitoring' ? 'Surveillance' : 'Vidéothèque'}</h2>
            <p className="kicker flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#00f2ea] shadow-[0_0_10px_#00f2ea]"></span>
              SYSTÈME EN LIGNE • POLL INTERVAL ~5 MIN
            </p>
          </div>

          {activeTab === 'monitoring' && (
            <div className="command-palette">
              <input 
                className="cmd-input" 
                placeholder="Ajouter une cible (ex: sato_tv)..." 
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addFavorite()}
              />
              <button onClick={addFavorite} className="cmd-submit">ADD</button>
            </div>
          )}
        </motion.header>

        {activeTab === 'monitoring' ? (
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="creator-grid"
          >
            <AnimatePresence>
              {favorites.map((user) => {
                const isLive = status[user]?.state === 'recording';
                return (
                  <motion.div 
                    key={user}
                    variants={itemVariants}
                    layout
                    className={`cinematic-card ${isLive ? 'recording' : 'offline'}`}
                  >
                    <button onClick={() => removeFavorite(user)} className="glass-delete" title="Retirer">
                      <Trash2 size={16} strokeWidth={2} />
                    </button>

                    <div className="avatar-ring">
                      {status[user]?.avatar && !imgError[user] ? (
                         <img 
                           src={`${API_BASE}${status[user].avatar}`} 
                           className="avatar-image" 
                           alt={user} 
                           onError={() => setImgError(p => ({...p, [user]: true}))}
                         />
                      ) : (
                        <div className="avatar-image flex items-center justify-center bg-[#111]">
                          <User size={40} className="text-[#333]" />
                        </div>
                      )}
                      
                      {isLive && (
                         <div className="recording-badge">REC</div>
                      )}
                    </div>

                    <div className="text-center w-full">
                      <p className="text-lg font-black text-white uppercase tracking-tight truncate">@{user}</p>
                      {isLive ? (
                        <p className="kicker text-[#ff1a40] mt-1">Acquisition en cours...</p>
                      ) : (
                        <p className="kicker mt-1">Hors ligne</p>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
            
            {favorites.length === 0 && (
              <motion.div variants={itemVariants} className="col-span-full py-40 flex flex-col items-center justify-center opacity-40">
                <User size={64} className="mb-6 text-[#444] animate-bounce" strokeWidth={1} />
                <p className="kicker text-lg">AUCUNE CIBLE</p>
              </motion.div>
            )}
          </motion.div>
        ) : (
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="video-grid"
          >
            <AnimatePresence>
              {recordings.map((rec) => (
                <motion.div 
                  key={rec.name}
                  variants={itemVariants}
                  layout
                  className="video-card"
                >
                  <div className="video-frame group">
                    <video src={`${API_BASE}${rec.url}`} preload="metadata" />
                    <div className="video-overlay">
                      <a href={`${API_BASE}${rec.url}`} download className="action-crystal" title="Télécharger">
                         <Download size={20} strokeWidth={2.5} />
                      </a>
                      <button onClick={() => deleteRecording(rec.name)} className="action-crystal danger" title="Supprimer">
                         <Trash2 size={20} strokeWidth={2.5} />
                      </button>
                    </div>
                  </div>
                  <div className="video-info">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-black text-white uppercase mb-1">@{rec.name.split('_')[0]}</p>
                        <p className="kicker">
                          {new Date(rec.date).toLocaleDateString()} • {new Date(rec.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                        <Play size={16} className="text-white/40" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            
            {recordings.length === 0 && (
              <motion.div variants={itemVariants} className="col-span-full py-40 flex flex-col items-center justify-center opacity-40">
                 <Monitor size={64} className="mb-6 text-[#444]" strokeWidth={1} />
                 <p className="kicker text-lg">ARCHIVE VIDE</p>
              </motion.div>
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
}

export default App;
